import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Save, RotateCcw, Users, DollarSign, Clock, Globe } from 'lucide-react';
import { Card, Badge, Button } from '../../components/ui';
import { api } from '../../lib/api';

interface ComplianceRules {
  category: string;
  maxInvestorsPerOffering: number;
  minInvestmentAmount: number;
  maxInvestmentAmount: number;
  lockUpPeriodDays: number;
  requireQualifiedInvestor: boolean;
  allowedCountries: string[];
  restrictedCountries: string[];
  kycExpirationDays: number;
  transferRestrictions: {
    requireWhitelist: boolean;
    maxTransfersPerDay: number;
    cooldownPeriodHours: number;
  };
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundos',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios'
};

const defaultRules: ComplianceRules = {
  category: '',
  maxInvestorsPerOffering: 50,
  minInvestmentAmount: 100000, // R$ 1.000
  maxInvestmentAmount: 100000000, // R$ 1.000.000
  lockUpPeriodDays: 0,
  requireQualifiedInvestor: false,
  allowedCountries: ['BR'],
  restrictedCountries: [],
  kycExpirationDays: 365,
  transferRestrictions: {
    requireWhitelist: true,
    maxTransfersPerDay: 10,
    cooldownPeriodHours: 0
  }
};

export function ComplianceRulesConfig() {
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState('STABLECOIN');
  const [rules, setRules] = useState<ComplianceRules>({ ...defaultRules, category: 'STABLECOIN' });
  const [hasChanges, setHasChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { data: savedRules, isLoading } = useQuery({
    queryKey: ['compliance-rules', selectedCategory],
    queryFn: async () => {
      const res = await api.get<ComplianceRules>(`/tokenization/compliance/rules/${selectedCategory}`);
      return res.data;
    },
  });

  // Update rules when savedRules loads
  if (savedRules && !initialized) {
    setRules(savedRules);
    setHasChanges(false);
    setInitialized(true);
  }

  const saveMutation = useMutation({
    mutationFn: (data: ComplianceRules) => 
      api.put(`/tokenization/compliance/rules/${selectedCategory}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-rules'] });
      setHasChanges(false);
    }
  });

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setRules({ ...defaultRules, category });
  };

  const updateRules = (updates: Partial<ComplianceRules>) => {
    setRules(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="h-96 bg-surface-800 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Regras de Compliance</h1>
          <p className="text-surface-400">Configure as regras de compliance por categoria de ativo</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => setRules(savedRules || { ...defaultRules, category: selectedCategory })}
            disabled={!hasChanges}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reverter
          </Button>
          <Button
            onClick={() => saveMutation.mutate(rules)}
            disabled={!hasChanges || saveMutation.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            Salvar
          </Button>
        </div>
      </div>

      {/* Category Selector */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(categoryLabels).map(([value, label]) => (
          <button
            key={value}
            onClick={() => handleCategoryChange(value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === value
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/50'
                : 'bg-surface-800 text-surface-400 border border-surface-700 hover:border-surface-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investment Limits */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-primary-400" />
            <h3 className="font-semibold text-surface-100">Limites de Investimento</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-surface-400 mb-1">Investimento Mínimo</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">R$</span>
                <input
                  type="number"
                  value={rules.minInvestmentAmount / 100}
                  onChange={(e) => updateRules({ minInvestmentAmount: Number(e.target.value) * 100 })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg pl-10 pr-4 py-2 text-surface-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Investimento Máximo</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">R$</span>
                <input
                  type="number"
                  value={rules.maxInvestmentAmount / 100}
                  onChange={(e) => updateRules({ maxInvestmentAmount: Number(e.target.value) * 100 })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg pl-10 pr-4 py-2 text-surface-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Máximo de Investidores por Oferta</label>
              <input
                type="number"
                value={rules.maxInvestorsPerOffering}
                onChange={(e) => updateRules({ maxInvestorsPerOffering: Number(e.target.value) })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="qualifiedInvestor"
                checked={rules.requireQualifiedInvestor}
                onChange={(e) => updateRules({ requireQualifiedInvestor: e.target.checked })}
                className="rounded border-surface-600"
              />
              <label htmlFor="qualifiedInvestor" className="text-sm text-surface-300">
                Exigir Investidor Qualificado
              </label>
            </div>
          </div>
        </Card>

        {/* Time Restrictions */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-primary-400" />
            <h3 className="font-semibold text-surface-100">Restrições de Tempo</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-surface-400 mb-1">Período de Lock-up (dias)</label>
              <input
                type="number"
                value={rules.lockUpPeriodDays}
                onChange={(e) => updateRules({ lockUpPeriodDays: Number(e.target.value) })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                min={0}
              />
              <p className="text-xs text-surface-500 mt-1">0 = sem lock-up</p>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Validade do KYC (dias)</label>
              <input
                type="number"
                value={rules.kycExpirationDays}
                onChange={(e) => updateRules({ kycExpirationDays: Number(e.target.value) })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                min={30}
              />
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Cooldown entre Transferências (horas)</label>
              <input
                type="number"
                value={rules.transferRestrictions.cooldownPeriodHours}
                onChange={(e) => updateRules({ 
                  transferRestrictions: { 
                    ...rules.transferRestrictions, 
                    cooldownPeriodHours: Number(e.target.value) 
                  }
                })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                min={0}
              />
            </div>
          </div>
        </Card>

        {/* Transfer Restrictions */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-primary-400" />
            <h3 className="font-semibold text-surface-100">Restrições de Transferência</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requireWhitelist"
                checked={rules.transferRestrictions.requireWhitelist}
                onChange={(e) => updateRules({ 
                  transferRestrictions: { 
                    ...rules.transferRestrictions, 
                    requireWhitelist: e.target.checked 
                  }
                })}
                className="rounded border-surface-600"
              />
              <label htmlFor="requireWhitelist" className="text-sm text-surface-300">
                Exigir Whitelist para Transferências
              </label>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Máximo de Transferências por Dia</label>
              <input
                type="number"
                value={rules.transferRestrictions.maxTransfersPerDay}
                onChange={(e) => updateRules({ 
                  transferRestrictions: { 
                    ...rules.transferRestrictions, 
                    maxTransfersPerDay: Number(e.target.value) 
                  }
                })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                min={1}
              />
            </div>
          </div>
        </Card>

        {/* Geographic Restrictions */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-primary-400" />
            <h3 className="font-semibold text-surface-100">Restrições Geográficas</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-surface-400 mb-1">Países Permitidos</label>
              <input
                type="text"
                value={rules.allowedCountries.join(', ')}
                onChange={(e) => updateRules({ 
                  allowedCountries: e.target.value.split(',').map(c => c.trim().toUpperCase()).filter(Boolean)
                })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                placeholder="BR, US, PT"
              />
              <p className="text-xs text-surface-500 mt-1">Códigos ISO separados por vírgula</p>
            </div>

            <div>
              <label className="block text-sm text-surface-400 mb-1">Países Restritos</label>
              <input
                type="text"
                value={rules.restrictedCountries.join(', ')}
                onChange={(e) => updateRules({ 
                  restrictedCountries: e.target.value.split(',').map(c => c.trim().toUpperCase()).filter(Boolean)
                })}
                className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                placeholder="KP, IR, CU"
              />
              <p className="text-xs text-surface-500 mt-1">Países bloqueados (OFAC, etc.)</p>
            </div>
          </div>
        </Card>
      </div>

      {hasChanges && (
        <div className="fixed bottom-6 right-6 bg-yellow-500/20 border border-yellow-500/50 rounded-lg px-4 py-2 flex items-center gap-2">
          <span className="text-yellow-400 text-sm">Alterações não salvas</span>
        </div>
      )}
    </div>
  );
}
